import React, { useState } from "react";
import { launchGoogleAuthPopup, ReauthResult } from "../lib/authLib";

interface ReauthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (result: ReauthResult) => void;
  inboxId: string;
  inboxEmail: string;
  reason: "token_expiring" | "insufficient_scopes";
}

type ModalState = "idle" | "waiting" | "error";

const ReauthModal: React.FC<ReauthModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  inboxId: _inboxId,
  inboxEmail,
  reason,
}) => {
  const [state, setState] = useState<ModalState>("idle");
  const [errorMessage, setErrorMessage] = useState<string>("");

  if (!isOpen) return null;

  const handleReauthorize = async () => {
    setState("waiting");
    setErrorMessage("");

    try {
      const result = await launchGoogleAuthPopup(inboxEmail, "reauth");
      onSuccess(result);
    } catch (error) {
      if (error instanceof Error) {
        if (error.message === "popup_blocked") {
          setErrorMessage(
            "The popup was blocked by your browser. Please allow popups for this site and try again."
          );
        } else if (error.message === "popup_closed") {
          setErrorMessage(
            "The authorization window was closed before completing. Click below to try again."
          );
        } else {
          setErrorMessage("Authorization failed. Please try again.");
        }
      } else {
        setErrorMessage("An unexpected error occurred. Please try again.");
      }
      setState("error");
    }
  };

  const title =
    reason === "insufficient_scopes"
      ? "Missing Required Permissions"
      : "Token Refresh Required";

  const description =
    reason === "insufficient_scopes"
      ? "Your account is missing required Gmail permissions. Cobutler filtering is currently paused."
      : "Your Gmail authorization is expiring soon. Re-authorize to keep Cobutler filtering active.";

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start">
          <svg
            className="h-12 w-12 text-yellow-400 mr-4 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              {title}
            </h2>

            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
              {description}
            </p>

            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Account: <span className="font-medium">{inboxEmail}</span>
            </p>

            {reason === "insufficient_scopes" && (
              <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded mb-4">
                <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                  What's affected:
                </p>
                <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 list-disc list-inside">
                  <li>Email filtering is paused</li>
                  <li>Rules cannot be applied</li>
                  <li>Challenge system is inactive</li>
                </ul>
              </div>
            )}

            {state === "waiting" && (
              <div className="bg-blue-50 dark:bg-blue-900 p-3 rounded mb-4 flex items-center">
                <svg
                  className="animate-spin h-5 w-5 text-blue-600 dark:text-blue-300 mr-3"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Waiting for Google authorization...
                </p>
              </div>
            )}

            {state === "error" && (
              <div className="bg-red-50 dark:bg-red-900 p-3 rounded mb-4">
                <p className="text-sm text-red-800 dark:text-red-200">
                  {errorMessage}
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={handleReauthorize}
                disabled={state === "waiting"}
                className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-4 py-2 rounded font-medium transition-colors"
              >
                {state === "error" ? "Try Again" : "Reconnect Account"}
              </button>
              <button
                onClick={onClose}
                disabled={state === "waiting"}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              >
                Later
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReauthModal;
